import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import surveyData from "./templatesData"; // Assuming surveyData.js is in the same directory
import { Modal, Button, TextInput, Flex, Image } from '@mantine/core';
import logo from '../../assets/logo.png';
import { SurveyCreatorComponent, SurveyCreator, Serializer } from '@navriti/forms';
import {
  FETCH_TEMPLATE_URL,
  SAVE_TEMPLATE_URL,
  UPDATE_TEMPLATE_URL,
  SAVE_FORM_URL,
  SUCCESS_MESSAGE,
  ERROR_MESSAGE_FETCH,
  ERROR_MESSAGE_SAVE,
  WARNING_NO_SURVEY,
  ERROR_NO_TOKEN,
  ERROR_FETCH_FAIL,
  ERROR_SAVE_FAIL
} from '../messages.js/buildTemplateTexts'; // Importing constants from config.js

// Function to get survey by title from local surveyData
function getSurveyByTitle(formName) {
  if (!formName) {
    throw new Error("Missing 'formName' parameter in URL");
  }

  const survey = surveyData.find(survey => survey.TemplateName === formName);
  return survey ? JSON.parse(survey.TemplateData) : null;
}

// Function to get token from cookies
function getTokenFromCookie() {
  const cookies = document.cookie.split(';');
  const tokenCookie = cookies.find(cookie => cookie.trim().startsWith('token='));
  return tokenCookie ? tokenCookie.split('=')[1] : null;
}

export default function SurveyCreatorWidget() {
  const [load, setLoad] = useState(false);
  const [creator, setCreator] = useState(null); // State to store SurveyCreator instance
  const [formData, setFormData] = useState(null); // State to store form data
  const [error, setError] = useState(null); // State to store error message
  const location = useLocation(); // Get current location
  const navigate = useNavigate(); // Get navigate function from React Router

  // Updated useEffect in SurveyCreatorWidget.jsx
useEffect(() => {
  const initializeSurveyCreator = async () => {
    try {
      const params = new URLSearchParams(location.search);
      const name = params.get('formName');
      const templateName = params.get('templateName');
      const useTemplate = params.get('useTemplate');
      
      let survey = null;
      let targetTemplateName = name || templateName;
      
      // First try to find in local surveyData
      if (targetTemplateName) {
        survey = getSurveyByTitle(targetTemplateName);
      }
      
      if (!survey) {
        // If not found locally, fetch from API
        const token = getTokenFromCookie();
        if (token) {
          const response = await fetch(FETCH_TEMPLATE_URL, {
            method: "POST",
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ email: getUserEmailFromToken(token) }) // You'll need this helper function
          });
          
          if (response.ok) {
            const fetchedData = await response.json();
            
            if (fetchedData && fetchedData.templateData && Array.isArray(fetchedData.templateData)) {
              const matchingSurvey = fetchedData.templateData.find(item => 
                item.TemplateName === targetTemplateName
              );
              
              if (matchingSurvey) {
                survey = matchingSurvey.TemplateData;
                if (typeof survey === 'string') {
                  survey = JSON.parse(survey);
                }
              } else {
                throw new Error('No matching survey found in fetched data');
              }
            } else {
              throw new Error('Fetched data is not structured as expected');
            }
          } else {
            throw new Error(ERROR_FETCH_FAIL);
          }
        } else {
          throw new Error(ERROR_NO_TOKEN);
        }
      }
      
      if (survey) {
        const surveyCreator = new SurveyCreator({ showLogicTab: true, isAutoSave: true });
        Serializer.findProperty("file", "allowMultiple").visible = true;
        const hiddenTypes = [
            "multipletext",
            "paneldynamic",
            "matrix",
            "matrixdropdown",
            "matrixdynamic",
            "matrixrating",
            "imagepicker"
          ];

          hiddenTypes.forEach(type => {
            surveyCreator.toolbox.removeItem(type);
          });

        // ✅ Auto-trim survey description to max 1000 characters
        surveyCreator.onPropertyValueChanging?.add((sender, options) => {
          if (options.propertyName === "description") {
            const maxLength = 1000;
            const newValue = options.newValue || "";

            if (newValue.length > maxLength) {
              options.newValue = newValue.substring(0, maxLength);
              toast.warn("Description trimmed to 1000 characters.");
              //options.error = `Description cannot exceed ${maxLength} characters. Currently: ${value.length}`;
            }
          }
        });
          
        
        // If using template for new form creation, modify the title
        if (useTemplate === 'true') {
          const surveyObj = typeof survey === 'string' ? JSON.parse(survey) : survey;
          surveyObj.title = `Copy of ${surveyObj.title || targetTemplateName}`;
          survey = JSON.stringify(surveyObj);
        }
        
        surveyCreator.text = typeof survey === 'string' ? survey : JSON.stringify(survey);
        setFormData(survey); 
        setCreator(surveyCreator);
        setLoad(true);
      } else {
        console.warn(WARNING_NO_SURVEY);
        setError(WARNING_NO_SURVEY);
      }
    } catch (error) {
      console.error("Error:", error.message);
      setError(ERROR_MESSAGE_FETCH);
    }
  };

  initializeSurveyCreator();
}, [location.search]);

// Helper function to extract email from token (you may need to implement this)
function getUserEmailFromToken(token) {
  try {
    // Decode JWT token to get user email
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.email;
  } catch (error) {
    console.error('Error decoding token:', error);
    return null;
  }
}

  // Save template data function
  const saveTemplateData = async (isDraft) => {
    try {
      const token = getTokenFromCookie();
      const creatorJSON = JSON.parse(creator.text);
      const response = await fetch(SAVE_TEMPLATE_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        credentials: 'include',
        body: JSON.stringify({
          TemplateData: creatorJSON,
          TemplateName: creatorJSON['title'],
          isDraft: isDraft
        })
      });
      if (response.ok) {
        toast.success(SUCCESS_MESSAGE, {
          onClose: () => navigate('/') // Navigate to home.jsx after successful toast
        });
        let res = await response.json();
      } else {
        toast.error(ERROR_SAVE_FAIL);
      }
    } catch (error) {
      console.error("Error saving template data:", error);
      toast.error(ERROR_MESSAGE_SAVE);
    }
  };


  // Function to handle navigation back to home page
  const handleBack = () => {
    navigate("/", { state: { navItem: "Templates" },
      replace: true
     });
  };

  // Function to handle save button click
  const handleSaveButtonClick = () => {
    saveTemplateData(false);
  };
  const handleSaveDraftButtonClick=()=>
    {
      saveTemplateData(true);
    }

  // Improved conditional rendering with error handling
  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      {error ? (
        <p style={{ color: 'red', fontSize: '25px', textAlign: 'center', marginTop: '20px' }}>
          Error: {error}
        </p>
      ) : load && creator && formData ? (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '0 24px',
              backgroundColor: '#ffffff',
              borderBottom: '1px solid #ddd',
              boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
              position: 'sticky',
              top: 0,
              zIndex: 10,
              height: '60px',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <Flex align="center">
                <a href="/"><Image src={logo} height={50} width={50} /></a>
              </Flex>
              <h2 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 600, color: '#333' }}>
                Template Builder
              </h2>
            </div>

            <div style={{ display: 'flex', gap: '6px', flexShrink: 0, height: '60%' }}>
              <Button
                onClick={handleBack}
                variant="outline"
                style={{ borderRadius: '9px', height: '100%' }}
              >
                Back to Templates
              </Button>

              <Button
                onClick={handleSaveDraftButtonClick}
                variant="outline"
                style={{ borderRadius: '9px', height: '100%' }}
              >
                Save as Draft
              </Button>

              <Button
                onClick={handleSaveButtonClick}
                variant="outline"
                style={{ borderRadius: '9px', height: '100%' }}
              >
                Save Template
              </Button>
            </div>
          </div>

          <div style={{ flex: 1 }}>
            <SurveyCreatorComponent creator={creator} />
          </div>
          <ToastContainer />
        </div>
      ) : (
        <p style={{color: 'gray', fontSize: '25px', textAlign: 'center', marginTop: '20px'}}>Loading...</p>
      )}
    </div>
  );
}